import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import * as Yup from 'yup';
import { faHandHoldingBox, faTruck, faWarehouse } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useFormDebug } from '@spacefill/shared/src/hooks/useFormDebug';
import { call } from '@spacefill/shared/src/utils/Backend';
import { getDisplayableErrorMessage } from '@spacefill/shared/src/utils/Error';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { FieldError } from '@spacefill/uikit/src/components/FieldError/FieldError';
import { Modal } from '@spacefill/uikit/src/components/Modal';
import { toast } from '@spacefill/uikit/src/components/Toast/createToast';
import { cn } from '@spacefill/uikit/src/lib/utils';
import { RHFCheckbox } from '@spacefill/uikit/src/rhf/RHFCheckbox/RHFCheckbox';
import { RHFCommandMultiSelect } from '@spacefill/uikit/src/rhf/RHFCommandMultiSelect/RHFCommandMultiSelect';
import { RHFFileInput } from '@spacefill/uikit/src/rhf/RHFFileInput/RHFFileInput';
import { RHFTextAreaField } from '@spacefill/uikit/src/rhf/RHFTextAreaField/RHFTextAreaField';
import { RHFTextField } from '@spacefill/uikit/src/rhf/RHFTextField/RHFTextField';

export function CreateAnnouncementModal({ isOpen, onClose, onSuccess }) {
    const { t } = useTranslation();
    const { user } = useUserContext();
    const queryClient = useQueryClient();
    const [pendingBroadcastPayload, setPendingBroadcastPayload] = useState(null);

    const isShipper = user.role === 'SHIPPER' || Boolean(user.customerId);
    const isProvider = user.role === 'PROVIDER' || Boolean(user.logisticProviderId);

    const warehouseItems = (user.warehouses ?? []).map((warehouse) => ({
        id: warehouse.id,
        label: warehouse.label,
    }));

    const customerItems = (user.customers ?? []).map((customer) => ({
        id: customer.id,
        label: customer.company,
    }));

    const customerCarriersItems = user.customerCarriers.map((customerCarrier) => ({
        id: customerCarrier.id,
        label: customerCarrier.carrier?.company,
    }));

    const { mutate, isPending } = useMutation({
        mutationFn: async (data) => {
            return call('/in-app-notifications/publish-announcement', data, 'POST');
        },
        async onSuccess() {
            await queryClient.invalidateQueries({ queryKey: ['/in-app-notifications/list-announcements'] });
            toast.success(t('Announcement has been published.'));
            await onSuccess();
        },
        onError(error) {
            console.error('error publishing announcement: ', error);
            toast.error(getDisplayableErrorMessage(error));
        },
    });

    const formMethods = useForm({
        mode: 'onSubmit',
        resolver: yupResolver(
            Yup.object().shape({
                announcement: Yup.object().shape({
                    title: Yup.string().required(t('{{name}} is required.', { name: t('Title') })),
                    content: Yup.string().required(t('{{name}} is required.', { name: t('Content') })),
                }),
                recipients: Yup.object()
                    .shape({
                        includeMyCompany: Yup.boolean(),
                        includeWarehouses: Yup.boolean(),
                        logisticProviderWarehouseIds: Yup.array().of(Yup.string()),
                        includeCustomers: Yup.boolean(),
                        customerIds: Yup.array().of(Yup.string()),
                        includeCarriers: Yup.boolean(),
                        customerCarrierIds: Yup.array().of(Yup.string()),
                        sendToAllShippers: Yup.boolean(),
                        sendToAllLogisticProviders: Yup.boolean(),
                    })
                    .test('recipient-required', t('At least one recipient is required'), function (value) {
                        const includeMyCompany = Boolean(value?.includeMyCompany);
                        const includeWarehouses = Boolean(value?.includeWarehouses);
                        const logisticProviderWarehouseIds = value?.logisticProviderWarehouseIds ?? [];

                        const includeCustomers = Boolean(value?.includeCustomers);
                        const customerIds = value?.customerIds ?? [];
                        const includeCarriers = Boolean(value?.includeCarriers);
                        const customerCarrierIds = value?.customerCarrierIds ?? [];

                        if (includeMyCompany) {
                            return true;
                        }

                        if (isShipper && includeWarehouses && logisticProviderWarehouseIds.length > 0) {
                            return true;
                        }

                        if (isProvider && includeCustomers && customerIds.length > 0) {
                            return true;
                        }

                        if (includeCarriers && customerCarrierIds.length > 0) {
                            return true;
                        }

                        if (value?.sendToAllShippers || value?.sendToAllLogisticProviders) {
                            return true;
                        }

                        return false;
                    }),
            })
        ),
        defaultValues: {
            announcement: {
                title: '',
                content: '',
                announcementFiles: [],
            },
            recipients: {
                includeMyCompany: false,
                includeWarehouses: false,
                logisticProviderWarehouseIds: [],
                includeCustomers: false,
                customerIds: [],
                includeCarriers: false,
                customerCarrierIds: [],
                sendToAllShippers: false,
                sendToAllLogisticProviders: false,
            },
        },
    });

    const includeWarehouses = formMethods.watch('recipients.includeWarehouses');
    const includeCustomers = formMethods.watch('recipients.includeCustomers');
    const includeCarriers = formMethods.watch('recipients.includeCarriers');

    function onSubmit(data) {
        const recipientIdsByType = {
            logisticProviderWarehouseIds: [],
            customerIds: [],
            logisticProviderIds: [],
            customerCarrierIds: [],
        };

        if (data.recipients.includeMyCompany) {
            if (isShipper && user.customerId) {
                recipientIdsByType.customerIds.push(user.customerId);
            }
            if (isProvider && user.logisticProviderId) {
                recipientIdsByType.logisticProviderIds.push(user.logisticProviderId);
            }
        }

        if (isShipper && data.recipients.includeWarehouses) {
            recipientIdsByType.logisticProviderWarehouseIds.push(
                ...(data.recipients.logisticProviderWarehouseIds ?? [])
            );
        }

        if (isProvider && data.recipients.includeCustomers) {
            recipientIdsByType.customerIds.push(...(data.recipients.customerIds ?? []));
        }

        if (data.recipients.includeCarriers) {
            recipientIdsByType.customerCarrierIds.push(...(data.recipients.customerCarrierIds ?? []));
        }

        recipientIdsByType.logisticProviderWarehouseIds = [...new Set(recipientIdsByType.logisticProviderWarehouseIds)];
        recipientIdsByType.customerIds = [...new Set(recipientIdsByType.customerIds)];
        recipientIdsByType.logisticProviderIds = [...new Set(recipientIdsByType.logisticProviderIds)];
        recipientIdsByType.customerCarrierIds = [...new Set(recipientIdsByType.customerCarrierIds)];

        const payload = {
            announcement: {
                creatorName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
                title: data.announcement.title,
                content: data.announcement.content,
                recipientIdsByType,
            },
            announcementFiles: data.announcement.announcementFiles,
            ...recipientIdsByType,
            sendToAllShippers: user.canPublishAnnouncementsGlobally
                ? Boolean(data.recipients.sendToAllShippers)
                : false,
            sendToAllLogisticProviders: user.canPublishAnnouncementsGlobally
                ? Boolean(data.recipients.sendToAllLogisticProviders)
                : false,
        };

        if (payload.sendToAllShippers || payload.sendToAllLogisticProviders) {
            setPendingBroadcastPayload(payload);
            return;
        }

        mutate(payload);
    }

    function handleConfirmBroadcast() {
        if (pendingBroadcastPayload) {
            mutate(pendingBroadcastPayload);
        }
        setPendingBroadcastPayload(null);
    }

    function handleClose() {
        setPendingBroadcastPayload(null);
        onClose();
    }

    useFormDebug(formMethods);

    const broadcastsToShippers = Boolean(pendingBroadcastPayload?.sendToAllShippers);
    const broadcastsToLogisticProviders = Boolean(pendingBroadcastPayload?.sendToAllLogisticProviders);
    const broadcastConfirmMessage =
        broadcastsToShippers && broadcastsToLogisticProviders
            ? t('You are about to share this announcement with all shipper and 3PL accounts.')
            : broadcastsToShippers
              ? t('You are about to share this announcement with all shipper accounts.')
              : t('You are about to share this announcement with all 3PL accounts.');

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={handleClose}
                size='lg'
                title={t('New announcement')}
                confirmLabel={t('Publish')}
                isProcessing={isPending}
                confirmButtonProps={{
                    type: 'submit',
                    htmlAttributes: {
                        form: 'create-announcement',
                    },
                }}
            >
                <FormProvider {...formMethods}>
                    <form
                        onSubmit={formMethods.handleSubmit(onSubmit)}
                        id='create-announcement'
                    >
                        <div className='flex flex-col gap-4 pt-2'>
                            <RHFTextField
                                name='announcement.title'
                                label={t('Annoucement title')}
                                placeholder={t('Enter announcement title')}
                            />
                            <RHFTextAreaField
                                name='announcement.content'
                                label={t('Content')}
                                rows={5}
                            />
                            <RHFFileInput name='announcement.announcementFiles' />

                            <div
                                className={cn(
                                    'flex flex-col gap-3 rounded-md border p-3',
                                    formMethods.formState.errors?.recipients?.message
                                        ? 'border-red-300'
                                        : 'border-gray-200'
                                )}
                            >
                                <RHFCheckbox
                                    name='recipients.includeMyCompany'
                                    label={
                                        isShipper ? (
                                            <>
                                                <FontAwesomeIcon icon={faHandHoldingBox} />{' '}
                                                {t('Send to my coworkers ({{company}})', {
                                                    company:
                                                        user.customer?.company ||
                                                        user.customers?.find(
                                                            (customer) => customer.id === user.customerId
                                                        )?.company ||
                                                        t('Unknown company'),
                                                })}
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faWarehouse} />{' '}
                                                {t('Send to my coworkers ({{company}})', {
                                                    company:
                                                        user.logisticProvider?.company ||
                                                        user.logisticProviders?.find((provider) => {
                                                            return provider.id === user.logisticProviderId;
                                                        })?.company ||
                                                        t('Unknown company'),
                                                })}
                                            </>
                                        )
                                    }
                                />

                                {isShipper && (
                                    <>
                                        <RHFCheckbox
                                            name='recipients.includeWarehouses'
                                            label={
                                                <>
                                                    <FontAwesomeIcon icon={faWarehouse} /> {t('Send to warehouses')}
                                                </>
                                            }
                                            onAfterChange={(checked) => {
                                                formMethods.setValue(
                                                    'recipients.logisticProviderWarehouseIds',
                                                    checked ? warehouseItems.map((warehouse) => warehouse.id) : [],
                                                    { shouldValidate: true }
                                                );
                                            }}
                                        />
                                        <RHFCommandMultiSelect
                                            name='recipients.logisticProviderWarehouseIds'
                                            items={warehouseItems}
                                            placeholder={
                                                includeWarehouses
                                                    ? t('Select one or more warehouses')
                                                    : t('No warehouse selected')
                                            }
                                            disabled={!includeWarehouses}
                                            mode='multiple'
                                            onAfterChange={(selectedIds) => {
                                                if (selectedIds.length === 0) {
                                                    formMethods.setValue('recipients.includeWarehouses', false, {
                                                        shouldValidate: true,
                                                    });
                                                }
                                            }}
                                        />
                                    </>
                                )}

                                {isProvider && (
                                    <>
                                        <RHFCheckbox
                                            name='recipients.includeCustomers'
                                            label={
                                                <>
                                                    <FontAwesomeIcon icon={faHandHoldingBox} /> {t('Send to shippers')}
                                                </>
                                            }
                                            onAfterChange={(checked) => {
                                                formMethods.setValue(
                                                    'recipients.customerIds',
                                                    checked ? customerItems.map((customer) => customer.id) : [],
                                                    {
                                                        shouldValidate: true,
                                                    }
                                                );
                                            }}
                                        />
                                        <RHFCommandMultiSelect
                                            name='recipients.customerIds'
                                            items={customerItems}
                                            placeholder={
                                                includeCustomers
                                                    ? t('Select one or more customers')
                                                    : t('No customers selected')
                                            }
                                            disabled={!includeCustomers}
                                            mode='multiple'
                                            onAfterChange={(selectedIds) => {
                                                if (selectedIds.length === 0) {
                                                    formMethods.setValue('recipients.includeCustomers', false, {
                                                        shouldValidate: true,
                                                    });
                                                }
                                            }}
                                        />
                                    </>
                                )}

                                <RHFCheckbox
                                    name='recipients.includeCarriers'
                                    label={
                                        <>
                                            <FontAwesomeIcon icon={faTruck} /> {t('Send to carriers')}
                                        </>
                                    }
                                    onAfterChange={(checked) => {
                                        formMethods.setValue(
                                            'recipients.customerCarrierIds',
                                            checked ? customerCarriersItems.map((carrier) => carrier.id) : [],
                                            {
                                                shouldValidate: true,
                                            }
                                        );
                                    }}
                                />
                                <RHFCommandMultiSelect
                                    name='recipients.customerCarrierIds'
                                    items={customerCarriersItems}
                                    placeholder={
                                        includeCarriers ? t('Select one or more carriers') : t('No carrier selected')
                                    }
                                    disabled={!includeCarriers}
                                    mode='multiple'
                                    onAfterChange={(selectedIds) => {
                                        if (selectedIds.length === 0) {
                                            formMethods.setValue('recipients.includeCarriers', false, {
                                                shouldValidate: true,
                                            });
                                        }
                                    }}
                                />

                                {user.canPublishAnnouncementsGlobally && (
                                    <>
                                        <RHFCheckbox
                                            name='recipients.sendToAllShippers'
                                            label={
                                                <>
                                                    <FontAwesomeIcon icon={faHandHoldingBox} />{' '}
                                                    {t('Send to all shippers')}
                                                </>
                                            }
                                        />
                                        <RHFCheckbox
                                            name='recipients.sendToAllLogisticProviders'
                                            label={
                                                <>
                                                    <FontAwesomeIcon icon={faWarehouse} /> {t('Send to all 3PLs')}
                                                </>
                                            }
                                        />
                                    </>
                                )}

                                {formMethods.formState.errors?.recipients?.message && (
                                    <FieldError>{formMethods.formState.errors.recipients.message}</FieldError>
                                )}
                            </div>
                        </div>
                    </form>
                </FormProvider>
            </Modal>

            <Modal
                isOpen={Boolean(pendingBroadcastPayload)}
                onClose={() => setPendingBroadcastPayload(null)}
                onConfirm={handleConfirmBroadcast}
                size='sm'
                title={t('Warning')}
                confirmLabel={t('Yes, publish')}
                isProcessing={isPending}
            >
                <p>{broadcastConfirmMessage}</p>
            </Modal>
        </>
    );
}
